/**
 * Authentication service for handling user authentication operations
 */

import axios from "axios";
import axiosClient, { ApiResponse } from "../../config/axiosClient";
import { API_CONFIG } from "../../config/env";
import { ENDPOINTS } from "../../config/endpoints";
import { objectToFormData } from "../../helpers/formDataHelper";
import {
  setAccessToken,
  setRefreshToken,
  getRefreshToken,
} from "../../../utils/storage";
import {
  notifyTokenRefreshFailed,
  notifyTokenRefreshStarting,
  notifyTokenRefreshSuccess,
} from "../../../utils/tokenRefreshDebug";

const REFRESH_URL = `${API_CONFIG.BASE_URL.replace(/\/+$/, "")}${ENDPOINTS.AUTH.REFRESH}`;
import {
  User,
  LoginCredentials,
  RegisterData,
  AuthResponse,
  LoginResponse,
  RegisterResponse,
  LogoutResponse,
  ProfileResponse,
  UpdateProfileRequest,
  UpdateProfileResponse,
  ChangePasswordRequest,
  ChangePasswordResponse,
  MeResponse,
  SyncRequest,
  SyncResponse,
} from "./auth.types";

class AuthService {
  /** Unwrap Nest transform + auth controller nested { data: { access_token, refresh_token, user } } */
  private mapLoginEnvelope(apiData: unknown): LoginResponse {
    const api = apiData as {
      data?: {
        data?: { access_token?: string; refresh_token?: string; user?: User };
        access_token?: string;
        refresh_token?: string;
        user?: User;
      };
    };
    const raw = api?.data?.data ?? api?.data ?? api ?? {};
    const payload = raw as {
      user?: User;
      access_token?: string;
      refresh_token?: string;
    };
    return {
      success: true,
      message: "OK",
      user: (payload.user ?? {}) as User,
      accessToken: payload.access_token ?? "",
      refreshToken: payload.refresh_token ?? "",
      expiresIn: 0,
    };
  }

  /**
   * Login user with email and password
   */
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response = await axiosClient.post<unknown>(
      ENDPOINTS.AUTH.LOGIN,
      credentials,
    );
    return this.mapLoginEnvelope(response.data);
  }

  /**
   * Login with Google ID token (from Google Sign-In). Returns same shape as login().
   * @param flow sign-in for login page; sign-up for registration (allows new users).
   */
  async loginWithGoogle(
    idToken: string,
    flow: 'sign-in' | 'sign-up' = 'sign-in',
  ): Promise<LoginResponse> {
    const response = await axiosClient.post<{
      success?: boolean;
      data?: {
        success?: boolean;
        data?: { access_token?: string; user?: any };
        access_token?: string;
        user?: any;
      };
    }>(ENDPOINTS.AUTH.GOOGLE, { idToken, flow });
    return this.mapLoginEnvelope(response.data);
  }

  /**
   * Register a new user (direct registration, no OAuth). Returns same shape as login.
   */
  async register(userData: RegisterData): Promise<RegisterResponse> {
    const response = await axiosClient.post<unknown>(
      ENDPOINTS.AUTH.REGISTER,
      userData,
    );
    return this.mapLoginEnvelope(response.data) as RegisterResponse;
  }

  /**
   * Send OTP to email for signup verification (step 1 of normal signup).
   */
  async sendSignupOtp(email: string): Promise<{ success: boolean; message: string }> {
    const normalizedEmail = email.trim().toLowerCase();
    const response = await axiosClient.post<unknown>(ENDPOINTS.AUTH.SEND_SIGNUP_OTP, {
      email: normalizedEmail,
    });
    return this.unwrapAuthMessageEnvelope(response.data);
  }

  /**
   * Verify signup OTP (after user enters OTP in step 1 of normal signup).
   * OTP is always sent as a string of exactly 6 digits (leading zeros preserved).
   */
  async verifySignupOtp(
    email: string,
    otp: string,
  ): Promise<{ success: boolean; message: string }> {
    const normalizedEmail = email.trim().toLowerCase();
    const normalizedOtp = String(otp).replace(/\D/g, "").slice(0, 6);
    if (import.meta.env.DEV) {
      console.log("[auth] verify-signup-otp payload", {
        email: normalizedEmail,
        otp: normalizedOtp,
      });
    }
    const response = await axiosClient.post<unknown>(ENDPOINTS.AUTH.VERIFY_SIGNUP_OTP, {
      email: normalizedEmail,
      otp: normalizedOtp,
    });
    return this.unwrapAuthMessageEnvelope(response.data);
  }

  /**
   * Request a password reset email (email/password accounts only).
   */
  async requestForgotPassword(email: string): Promise<{ success: boolean; message: string }> {
    const response = await axiosClient.post<unknown>(ENDPOINTS.AUTH.FORGOT_PASSWORD, {
      email: email.trim().toLowerCase(),
    });
    return this.unwrapAuthMessageEnvelope(response.data);
  }

  /**
   * Set a new password using the token from the reset email.
   */
  async resetPasswordWithToken(
    token: string,
    password: string,
  ): Promise<{ success: boolean; message: string }> {
    const response = await axiosClient.post<unknown>(ENDPOINTS.AUTH.RESET_PASSWORD, {
      token: token.trim(),
      password,
    });
    return this.unwrapAuthMessageEnvelope(response.data);
  }

  /** Nest TransformInterceptor wraps controllers as { success, data, timestamp }. */
  private unwrapAuthMessageEnvelope(body: unknown): {
    success: boolean;
    message: string;
  } {
    const b = body as {
      success?: boolean;
      data?: { success?: boolean; message?: string };
      message?: string;
    };
    const inner = b?.data;
    const message =
      (typeof inner?.message === "string" && inner.message) ||
      (typeof b?.message === "string" && b.message) ||
      "OK";

    if (typeof inner?.success === "boolean" && inner.success === false) {
      throw new Error(
        (typeof inner.message === "string" && inner.message) || "Verification failed",
      );
    }

    return { success: true, message };
  }

  /**
   * Check if a shop name is available (for signup form live check and shop rename).
   * Pass excludeCompanyId when editing an existing shop so the current name stays valid.
   */
  async checkShopNameAvailable(
    name: string,
    excludeCompanyId?: string,
  ): Promise<{ available: boolean }> {
    const response = await axiosClient.get<{
      success?: boolean;
      data?: { available: boolean };
    }>(ENDPOINTS.AUTH.CHECK_SHOP_NAME, {
      params: {
        name: (name ?? "").trim(),
        ...(excludeCompanyId ? { excludeCompanyId } : {}),
      },
    });
    const body = response.data;
    const data = body?.data ?? body;
    return { available: (data as { available?: boolean })?.available ?? false };
  }

  /**
   * Logout current user
   */
  async logout(): Promise<LogoutResponse> {
    const response = await axiosClient.post<LogoutResponse>(
      ENDPOINTS.AUTH.LOGOUT,
    );
    return response.data;
  }

  /**
   * Extend session: POST /auth/refresh with refresh_token body (no Bearer required).
   */
  async extendSession(): Promise<{
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
  }> {
    const rt = getRefreshToken();
    if (!rt) {
      notifyTokenRefreshFailed("No refresh token in storage.");
      throw new Error("Session could not be extended. Please sign in again.");
    }
    notifyTokenRefreshStarting(ENDPOINTS.AUTH.REFRESH);
    try {
      const response = await axios.post<{
        success?: boolean;
        data?: {
          access_token?: string;
          refresh_token?: string;
          expires_in?: number;
        };
      }>(
        REFRESH_URL,
        { refresh_token: rt },
        {
          headers: { "Content-Type": "application/json" },
          timeout: API_CONFIG.TIMEOUT,
        },
      );
      const wrapped = response.data;
      const inner = wrapped?.data ?? {};
      const access_token = inner.access_token;
      const newRefresh = inner.refresh_token;
      if (!access_token || typeof access_token !== "string") {
        notifyTokenRefreshFailed("Response missing access_token.");
        throw new Error("Session could not be extended. Please sign in again.");
      }
      setAccessToken(access_token);
      if (newRefresh && typeof newRefresh === "string") {
        setRefreshToken(newRefresh);
      }
      notifyTokenRefreshSuccess();
      return {
        accessToken: access_token,
        refreshToken: typeof newRefresh === "string" ? newRefresh : rt,
        expiresIn: typeof inner.expires_in === "number" ? inner.expires_in : 0,
      };
    } catch (e) {
      if (!(e instanceof Error) || !e.message.includes("Session could not")) {
        notifyTokenRefreshFailed(e instanceof Error ? e.message : "Network or API error.");
      }
      throw e;
    }
  }

  /**
   * @deprecated Use extendSession(); kept for callers expecting AuthResponse shape.
   */
  async refreshToken(): Promise<AuthResponse> {
    const { accessToken, expiresIn, refreshToken: newRt } = await this.extendSession();
    return {
      user: {} as User,
      accessToken,
      refreshToken: newRt,
      expiresIn,
    };
  }

  /**
   * Get current user profile
   */
  async getProfile(): Promise<ProfileResponse> {
    const response = await axiosClient.get<ProfileResponse>(
      ENDPOINTS.AUTH.PROFILE,
    );
    return response.data;
  }

  /**
   * Update user profile
   */
  async updateProfile(
    profileData: UpdateProfileRequest,
  ): Promise<UpdateProfileResponse> {
    const formData = objectToFormData(profileData);

    const response = await axiosClient.put<UpdateProfileResponse>(
      ENDPOINTS.USERS.UPDATE_PROFILE,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  }

  /**
   * Change user password
   */
  async changePassword(
    passwordData: ChangePasswordRequest,
  ): Promise<ChangePasswordResponse> {
    const response = await axiosClient.post<ChangePasswordResponse>(
      ENDPOINTS.USERS.CHANGE_PASSWORD,
      passwordData,
    );
    return response.data;
  }

  /**
   * Upload user avatar
   */
  async uploadAvatar(
    file: File,
  ): Promise<{ avatar: string; message: string; success: boolean }> {
    const formData = new FormData();
    formData.append("avatar", file);

    const response = await axiosClient.post<{
      avatar: string;
      message: string;
      success: boolean;
    }>(ENDPOINTS.USERS.AVATAR, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  }

  /**
   * Delete user avatar
   */
  async deleteAvatar(): Promise<{ message: string; success: boolean }> {
    const response = await axiosClient.delete<{
      message: string;
      success: boolean;
    }>(ENDPOINTS.USERS.AVATAR);
    return response.data;
  }

  /**
   * Verify email address
   */
  async verifyEmail(
    token: string,
  ): Promise<{ message: string; success: boolean }> {
    const response = await axiosClient.post<{
      message: string;
      success: boolean;
    }>(`${ENDPOINTS.AUTH.PROFILE}/verify-email`, { token });
    return response.data;
  }

  /**
   * Resend email verification
   */
  async resendEmailVerification(): Promise<{
    message: string;
    success: boolean;
  }> {
    const response = await axiosClient.post<{
      message: string;
      success: boolean;
    }>(`${ENDPOINTS.AUTH.PROFILE}/resend-verification`);
    return response.data;
  }

  /**
   * Get current user status and information
   */
  async getMe(): Promise<MeResponse> {
    const response = await axiosClient.get<{
      success: boolean;
      data: MeResponse;
    }>(ENDPOINTS.AUTH.ME);
    return response.data.data;
  }

  /**
   * Sync user data (create or update user)
   */
  async syncUser(userData: SyncRequest): Promise<SyncResponse> {
    const response = await axiosClient.post<{
      success: boolean;
      data: SyncResponse;
    }>(ENDPOINTS.AUTH.SYNC, userData);
    return response.data.data;
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;
